import { motion } from 'framer-motion';
import {
  Smartphone,
  QrCode,
  Monitor,
  CreditCard,
  BarChart3,
  ClipboardCheck,
  Tv,
} from 'lucide-react';

const products = [
  {
    icon: Smartphone,
    title: 'Mobile App Ordering',
    description:
      'Intuitive, self-service ordering with digital payments. Browse menus, customize orders, and pay securely — all from your smartphone.',
    features: [
      'Real-time menu updates',
      'Order scheduling',
      'Payment history',
      'Favorites & reorder',
    ],
    image:
      'https://s3-cc-prod.s3.ap-south-1.amazonaws.com/tistl/Mobile+App+Ordering+-+Latest+2.png',
    imageAlt: 'Mobile app ordering',
    color: 'bg-[#FFC000]',
  },
  {
    icon: QrCode,
    title: 'QR Code Ordering',
    description:
      'Instant access for registered and non-registered users. Simply scan, order, and pay — no app download required.',
    features: [
      'Table-side ordering',
      'Guest access',
      'Quick checkout',
      'Multi-counter ordering',
    ],
    image:
      'https://s3-cc-prod.s3.ap-south-1.amazonaws.com/tistl/qrcode.jpeg',
    imageAlt: 'QR code ordering',
    color: 'bg-[#0066CC]',
  },
  {
    icon: Monitor,
    title: 'Self-Order Digital Kiosk',
    description:
      'Touch-based ordering with SMS/receipt validation. Perfect for high-traffic areas with reduced wait times.',
    features: [
      'Touch interface',
      'Visual menu display',
      'Receipt printing',
      'SMS confirmation',
    ],
    image:
      'https://s3-cc-prod.s3.ap-south-1.amazonaws.com/tistl/self-order.jpeg',
    imageAlt: 'Digital kiosk',
    color: 'bg-[#F5A623]',
  },
  {
    icon: CreditCard,
    title: 'Meal Card System',
    description:
      'Secure, employee-linked transactions with real-time reporting. Supports subsidized meals and budget tracking.',
    features: [
      'RFID/NFC cards',
      'Balance tracking',
      'Subsidy management',
      'Auto-reload options',
    ],
    image:
      'https://s3-cc-prod.s3.ap-south-1.amazonaws.com/tistl/mealCard.jpeg',
    imageAlt: 'Meal card system',
    color: 'bg-[#FFC000]',
  },
  {
    icon: BarChart3,
    title: 'Reports Dashboard & Analytics',
    description:
      'Centralized visibility into sales, consumption, and cafeteria operations with actionable insights.',
    features: [
      'Real-time dashboards',
      'Custom reports',
      'Trend analysis',
      'Export capabilities',
    ],
    image:
      'https://s3-cc-prod.s3.ap-south-1.amazonaws.com/tistl/Reports+Dashboard.png',
    imageAlt: 'Analytics dashboard',
    color: 'bg-[#0066CC]',
  },
  {
    icon: ClipboardCheck,
    title: 'Digital Checklist',
    description:
      'Operational efficiency with digital task management for kitchen and service staff compliance.',
    features: [
      'Cafeteria checklist',
      'Completion tracking',
      'Audit trails',
      'Compliance reports',
    ],
    image:
      'https://s3-cc-prod.s3.ap-south-1.amazonaws.com/tistl/Digital+checklist.png',
    imageAlt: 'Digital checklist',
    color: 'bg-[#F5A623]',
  },
  {
    icon: Tv,
    title: 'Live Menu Display',
    description:
      'Dynamic digital signage showing real-time menu availability, prices, and nutritional information.',
    features: [
      'Real-time updates',
      'Nutritional info',
      'Wait time display',
      'Promotional content',
    ],
    image:
      'https://s3-cc-prod.s3.ap-south-1.amazonaws.com/tistl/livemenu.jpeg',
    imageAlt: 'Live menu display',
    color: 'bg-[#FFC000]',
  },
];

const Products = () => {
  return (
    <section
      id="products"
      className="section-padding bg-gray-50"
      data-testid="products-section"
    >
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="section-header"
        >
          <span className="text-[#0066CC] text-sm font-semibold uppercase tracking-wider mb-4 block">
            Products & Solutions
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Complete Cafeteria Ecosystem
          </h2>
          <div className="accent-bar mx-auto mb-6" />
          <p className="text-base md:text-lg text-gray-600 leading-relaxed">
            Explore our comprehensive suite of products designed to digitize
            every aspect of your cafeteria operations.
          </p>
        </motion.div>

        {/* Products List */}
        <div className="space-y-16 md:space-y-24">
          {products.map((product, index) => (
            <motion.div
              key={product.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
              data-testid={`product-row-${index}`}
            >
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 ${product.color} rounded-xl flex items-center justify-center`}>
                    <product.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {product.title}
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 text-lg">
                  {product.description}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {product.features.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      className="flex items-center gap-2 text-gray-700"
                    >
                      <div className="w-5 h-5 rounded-full bg-[#F5A623]/20 flex items-center justify-center flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-[#F5A623]" />
                      </div>
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Image */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="relative group">
                  <div className="rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={product.image}
                      alt={product.imageAlt}
                      className="w-full h-[280px] md:h-[400px] transition-transform duration-500 group-hover:scale-105"
                    />
                    {/* object-cover */}
                  </div>
                  <div
                    className={`absolute -z-10 w-full h-full rounded-2xl top-4 ${
                      index % 2 === 0
                        ? '-right-4 bg-[#0066CC]/10'
                        : '-left-4 bg-[#F5A623]/10'
                    }`}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mt-16 md:mt-24 p-8 md:p-12 bg-white rounded-3xl shadow-lg border border-gray-100"
          data-testid="products-cta"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Ready to Transform Your Workplace Dining Experience?
          </h3>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Empower your workplace dining with TISTL's smart, secure, and
            scalable cafeteria technology solutions.
          </p>
          <a
            href="#contact"
            className="btn-primary inline-flex items-center gap-2 text-lg"
            data-testid="products-contact-btn"
          >
            Contact Us Today
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Products;
